import { googleLogout } from '@react-oauth/google';
import { AlertCircle, LogOut, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GoogleLoginButton, { googleOAuthEnabled } from '../components/GoogleLoginButton';
import AppleLoginButton, { appleOAuthEnabled } from '../components/AppleLoginButton';
import Layout from '../components/Layout';
import OtpAuthFlow from '../components/auth/OtpAuthFlow';
import { intentAPI, userAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { clearAuthenticatedUser, storeAuthenticatedUser } from '../services/authStorage';

export default function AuthChoice() {
	const { requestId } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const linkToken = searchParams.get('linkToken') || '';
	const welcomePath = `/welcome/${requestId}${linkToken ? `?linkToken=${encodeURIComponent(linkToken)}` : ''}`;
	const [allowedContacts, setAllowedContacts] = useState(null);
	const [pageError, setPageError] = useState('');
	const [needsSignOut, setNeedsSignOut] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let active = true;
		const initialize = async () => {
			try {
				const intentResponse = linkToken
						? await intentAPI.getIntentByLink(linkToken)
						: await intentAPI.getIntent(requestId);
					if (!active) return;
					const intent = responseData(intentResponse);
					const contacts = Object.fromEntries(Object.entries(intent?.auth_contacts || {}).map(([method, contact]) => [method, {
					maskedValue: contact.masked_value || contact.maskedValue,
					locked: Boolean(contact.locked),
					requestBound: Boolean(contact.request_bound ?? contact.requestBound),
				}]));
				if (!Object.keys(contacts).length) throw new Error('No authentication contact is available for this request.');
				setAllowedContacts(contacts);

				if (localStorage.getItem('token') && localStorage.getItem('user_id')) {
					try {
						if (linkToken) await intentAPI.getIntent(requestId);
						navigate(`/review/${requestId}`, { replace: true });
						return;
					} catch {
						setNeedsSignOut(true);
					}
				}
			} catch (error) {
				if (active) setPageError(error.response?.data?.detail || error.message || 'This payment request is unavailable.');
			} finally {
				if (active) setLoading(false);
			}
		};
		initialize();
		return () => { active = false; };
	}, [linkToken, navigate, requestId]);

	const signOut = async () => {
		const refreshToken = localStorage.getItem('refresh_token');
		try { if (refreshToken) await userAPI.logout(refreshToken); } catch { /* local cleanup remains authoritative */ }
		googleLogout();
		clearAuthenticatedUser();
		setNeedsSignOut(false);
		setPageError('');
	};

	const finishAuthentication = async (response, context = {}) => {
		storeAuthenticatedUser(responseData(response), context);
		try {
			await intentAPI.getIntent(requestId);
			navigate(`/review/${requestId}`, { replace: true });
		} catch (error) {
			setPageError(error.response?.data?.detail || 'This payment request is unavailable.');
			throw error;
		}
	};

	const handleGoogleSuccess = async (tokenResponse) => {
		setPageError('');
		try {
			const response = await userAPI.socialLogin('google', tokenResponse.access_token);
			await finishAuthentication(response);
		} catch (error) {
			if (!error.response?.data?.detail) setPageError('Google sign-in cannot access this payment request.');
		}
	};

	const handleAppleSuccess = async (authorization) => {
		setPageError('');
		try {
			const response = await userAPI.appleLogin(authorization);
			await finishAuthentication(response);
		} catch (error) {
			setPageError(error.response?.data?.detail || 'Apple sign-in cannot access this payment request.');
		}
	};

	return (
		<Layout onBack={() => navigate(welcomePath)}>
			<div className="mx-auto w-full max-w-md space-y-6">
				<header className="text-center"><div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded bg-cyan-50"><ShieldCheck className="h-6 w-6 text-cyan-800" /></div><h1 className="text-2xl font-semibold text-slate-900">Verify your identity</h1><p className="mt-2 text-sm text-slate-600">Use a contact attached to this transfer request.</p></header>
				{pageError && <div role="status" className="flex gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{pageError}</div>}
				{loading && <p className="py-10 text-center text-sm text-slate-500">Loading sign-in options...</p>}
				{needsSignOut && (
					<div className="space-y-4"><p className="text-sm text-slate-700">Your current account cannot access this request. Sign out to verify one of the request contacts.</p><button type="button" onClick={signOut} className="btn-primary h-12 w-full"><LogOut className="mr-2 inline h-4 w-4" />Sign out and continue</button><button type="button" onClick={() => navigate('/')} className="btn-secondary h-12 w-full">Keep current account</button></div>
				)}
				{!loading && !needsSignOut && allowedContacts && (
					<>
						{allowedContacts.email && (googleOAuthEnabled || appleOAuthEnabled) && <div className="space-y-3">
							{googleOAuthEnabled && <GoogleLoginButton onSuccess={handleGoogleSuccess} onError={() => setPageError('Google sign-in failed. Try again.')} className="flex h-12 w-full items-center justify-center rounded border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50">Continue with Google</GoogleLoginButton>}
							{appleOAuthEnabled && <AppleLoginButton onSuccess={handleAppleSuccess} onError={() => setPageError('Apple sign-in failed. Try again.')} className="flex h-12 w-full items-center justify-center rounded bg-black font-semibold text-white hover:bg-slate-800">Continue with Apple</AppleLoginButton>}
							<div className="flex items-center gap-3 text-xs uppercase text-slate-400"><span className="h-px flex-1 bg-slate-200" /><span>or use a code</span><span className="h-px flex-1 bg-slate-200" /></div>
						</div>}
						<OtpAuthFlow allowedContacts={allowedContacts} initialMethod={Object.keys(allowedContacts)[0]} requestId={requestId} linkToken={linkToken} onAuthenticated={finishAuthentication} onCancel={() => navigate(welcomePath)} />
					</>
				)}
			</div>
		</Layout>
	);
}
